import { useEffect, useState, type ReactElement } from 'react'
import { useClientConfig } from '@dogdisk/shared'

/**
 * PWA install prompt banner.
 *
 * Two flavors based on platform:
 *   - Android / Chromium desktop: listen for `beforeinstallprompt`, intercept
 *     it, and present our own banner. User clicks "立即安装" → call
 *     `event.prompt()` and await `userChoice`.
 *   - iOS Safari (no beforeinstallprompt support): detect via UA + standalone
 *     flag, then show a custom banner explaining "添加到主屏幕" via the share
 *     sheet (Safari "Add to Home Screen").
 *
 * Behavior:
 *   - Banner only shows after the user has been on /files for ~30s
 *     (per spec — gives the app a chance to feel useful first).
 *   - When dismissed, write `pwa_install_dismissed=<epoch_ms>` to localStorage
 *     and suppress for `pwa_client.install_dismiss_cooldown_days` (from
 *     `/gapi/public/config/client`，默认 14 天；运营可远程调整无需发版)。
 *   - When already installed (display-mode: standalone or
 *     navigator.standalone), never show.
 */

const DISMISS_KEY = 'pwa_install_dismissed'
const SHOW_AFTER_MS = 30 * 1000 // 30s on /files

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true
  // iOS Safari
  const nav = window.navigator as Navigator & { standalone?: boolean }
  return nav.standalone === true
}

function isIOSSafari(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  const isIOS = /iPhone|iPad|iPod/.test(ua)
  // Exclude Chrome for iOS / Firefox for iOS — they all use WebKit but cannot
  // install PWAs via the standard share-sheet path that Safari does.
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua)
  return isIOS && isSafari
}

function recentlyDismissed(cooldownDays: number): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY)
    if (!raw) return false
    const ts = Number(raw)
    if (!Number.isFinite(ts)) return false
    const ttlMs = cooldownDays * 24 * 60 * 60 * 1000
    return Date.now() - ts < ttlMs
  } catch {
    return false
  }
}

function rememberDismissed(): void {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()))
  } catch {
    /* ignore — private mode etc. */
  }
}

function shouldGate(): boolean {
  // Spec says "first time on /files for 30s". Hash router URL examples:
  //   #/files
  //   #/files?foo=bar
  // We simply require the hash to start with '#/files'.
  if (typeof window === 'undefined') return false
  return window.location.hash.startsWith('#/files')
}

export function InstallPrompt(): ReactElement | null {
  const { config } = useClientConfig()
  const cooldownDays = config.pwa_client.install_dismiss_cooldown_days
  const [bipEvent, setBipEvent] = useState<BeforeInstallPromptEvent | null>(null)
  const [showAndroid, setShowAndroid] = useState(false)
  const [showIOS, setShowIOS] = useState(false)
  const [hidden, setHidden] = useState(false)

  // beforeinstallprompt for Android / desktop Chromium.
  useEffect(() => {
    if (isStandalone()) return
    if (recentlyDismissed(cooldownDays)) return

    const handler = (e: Event) => {
      e.preventDefault()
      setBipEvent(e as BeforeInstallPromptEvent)
    }
    window.addEventListener('beforeinstallprompt', handler)
    return () => {
      window.removeEventListener('beforeinstallprompt', handler)
    }
  }, [cooldownDays])

  // Delayed show: only after the user lingers on /files for SHOW_AFTER_MS.
  useEffect(() => {
    if (isStandalone() || recentlyDismissed(cooldownDays)) return

    let timer: ReturnType<typeof setTimeout> | null = null
    const tryArm = () => {
      if (!shouldGate()) return
      if (timer) return
      timer = setTimeout(() => {
        if (bipEvent) {
          setShowAndroid(true)
        } else if (isIOSSafari()) {
          setShowIOS(true)
        }
      }, SHOW_AFTER_MS)
    }

    tryArm()
    const onHashChange = () => tryArm()
    window.addEventListener('hashchange', onHashChange)
    return () => {
      window.removeEventListener('hashchange', onHashChange)
      if (timer) clearTimeout(timer)
    }
  }, [bipEvent, cooldownDays])

  if (hidden) return null
  if (!showAndroid && !showIOS) return null

  const dismiss = () => {
    rememberDismissed()
    setHidden(true)
  }

  const acceptAndroid = () => {
    if (!bipEvent) return
    void bipEvent.prompt().then(() => bipEvent.userChoice).then(() => {
      // Always hide after user makes a choice; remember dismissal either way.
      rememberDismissed()
      setHidden(true)
    })
  }

  if (showAndroid) {
    return (
      <div
        data-frame-id="JhVmw"
        data-testid="JhVmw"
        role="dialog"
        aria-label="install app"
        className="fixed inset-x-0 bottom-0 z-50 mx-auto max-w-md border-t border-gray-200 bg-white p-3 shadow-lg"
      >
        <p className="mb-2 text-sm font-medium">安装 绅士网盘 到主屏幕</p>
        <p className="mb-3 text-xs text-gray-600">
          像 App 一样打开，离线也能浏览已缓存的文件列表。
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={acceptAndroid}
            className="flex-1 rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            立即安装
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
          >
            稍后
          </button>
        </div>
      </div>
    )
  }

  // iOS Safari custom prompt
  return (
    <div
      data-frame-id="JhVmw"
      data-testid="JhVmw"
      role="dialog"
      aria-label="install app ios"
      className="fixed inset-x-0 bottom-0 z-50 mx-auto max-w-md border-t border-gray-200 bg-white p-3 shadow-lg"
    >
      <p className="mb-2 text-sm font-medium">把 绅士网盘 添加到主屏幕</p>
      <ol className="mb-3 space-y-1 text-xs text-gray-600">
        <li>
          1. 点击 Safari 底部的&nbsp;
          <span aria-hidden className="inline-block">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline align-text-bottom">
              <path d="M12 3v12" />
              <path d="M7 8l5-5 5 5" />
              <path d="M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" />
            </svg>
          </span>
          &nbsp;分享按钮
        </li>
        <li>2. 选择「添加到主屏幕」/ Add to Home Screen</li>
        <li>3. 点击右上角「添加」/ Add</li>
      </ol>
      <button
        type="button"
        onClick={dismiss}
        className="w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
      >
        知道了
      </button>
    </div>
  )
}
